/**
 * DQ Rules Service - Main Entry Point
 * 
 * Smart service loader that switches between mock and real API based on USE_BACKEND config.
 * Follows the established service pattern used throughout the application.
 */

import { USE_BACKEND } from '../config/env';
import { dqRuleApiService } from './dqRuleService.api';
import { dqRuleMockService } from './dqRuleService.mock';
import type {
    BatchCreateRulesRequest,
    BatchCreateRulesResponse,
    ModernBatchCreateRulesRequest,
    ModernBatchCreateRulesResponse,
    BackendDQRule,
    GetRulesResponse,
    UpdateRuleRequest,
    BatchDeleteRulesRequest,
    BatchDeleteRulesResponse
} from '../types/dqRules';

// Service interface for consistent API across mock and real implementations
export interface DQRuleService {
    // Legacy batch operations
    createRulesBatch(request: BatchCreateRulesRequest): Promise<BatchCreateRulesResponse>;

    // Modern batch operations with create-or-reuse logic
    createRulesBatchModern(request: ModernBatchCreateRulesRequest): Promise<ModernBatchCreateRulesResponse>;

    getRulesForDataObject(dataObjectId: string, options?: { limit?: number; offset?: number }): Promise<GetRulesResponse>;
    getAllAvailableRules(options?: { limit?: number; offset?: number }): Promise<GetRulesResponse>;
    getRuleById(ruleId: string): Promise<BackendDQRule>;
    updateRule(ruleId: string, updates: UpdateRuleRequest): Promise<BackendDQRule>;
    deleteRule(ruleId: string): Promise<void>;
    deleteRulesBatch(ruleIds: string[]): Promise<BatchDeleteRulesResponse>;
}

// Smart service loader - switches based on backend configuration
const dqRuleService: DQRuleService = USE_BACKEND ? dqRuleApiService : dqRuleMockService;

export { dqRuleService };

// Re-export types for convenience
export type {
    BatchCreateRulesRequest,
    BatchCreateRulesResponse,
    ModernBatchCreateRulesRequest,
    ModernBatchCreateRulesResponse,
    BackendDQRule,
    GetRulesResponse,
    UpdateRuleRequest,
    BatchDeleteRulesRequest,
    BatchDeleteRulesResponse
} from '../types/dqRules';